import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Announcement } from "@/components/icons";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import FileImage from "@/components/ui/file-image";
import Modal from "@/components/ui/modal";
import {
  getAnnouncementById,
  getLoginAnnouncements,
  saveAnnouncementPreference,
} from "../../services/client-announcements.api";
import {
  type ClientAnnouncement,
  type ClientAnnouncementListItem,
  mapAnnouncementListItemToClient,
} from "../../types/client-announcements.types";
import {
  LOGIN_ANNOUNCEMENT_PROMPT_KEY,
  getShownLoginAnnouncementIds,
  markLoginAnnouncementsShown,
} from "../../utils/loginAnnouncementPrompt";
import styles from "./LoginAnnouncementsModal.module.scss";

const INITIAL_INDEX = 0;

/**
 * Kontrol sayfa yüklenişi başına bir kez koşar. `ProtectedRoute` route
 * değişimlerinde yeniden mount olabildiği için modül seviyesinde tutuluyor;
 * aksi halde her gezinmede istek atardı.
 */
let hasCheckedThisPageLoad = false;

const LoginAnnouncementsModal: React.FC = () => {
  const { t } = useTranslation();
  const [announcements, setAnnouncements] = useState<ClientAnnouncement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(INITIAL_INDEX);
  const [hiddenById, setHiddenById] = useState<Record<string, boolean>>({});
  const [persistedHiddenById, setPersistedHiddenById] = useState<
    Record<string, boolean>
  >({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const hasPendingLoginPrompt =
      window.sessionStorage.getItem(LOGIN_ANNOUNCEMENT_PROMPT_KEY) === "1";
    // Girişte her zaman kontrol edilir; giriş dışı yüklemelerde sayfa başına
    // bir kez. Böylece yönetici oturum açıkken popup yayınladığında kullanıcının
    // çıkıp yeniden girmesi gerekmiyor — bir yenileme yetiyor.
    if (!hasPendingLoginPrompt && hasCheckedThisPageLoad) {
      return;
    }

    window.sessionStorage.removeItem(LOGIN_ANNOUNCEMENT_PROMPT_KEY);
    hasCheckedThisPageLoad = true;

    let cancelled = false;

    async function loadLoginAnnouncements() {
      try {
        const { items } = await getLoginAnnouncements();
        // Bu sekmede zaten gösterilenler elenir: aynı duyuru her F5'te tekrar
        // önüne çıkmasın, ama yeni yayınlanan popup görünsün.
        const alreadyShown = getShownLoginAnnouncementIds();
        const pending = items.filter(
          (item: ClientAnnouncementListItem) => !alreadyShown.has(item.id),
        );
        if (cancelled || pending.length === 0) {
          return;
        }

        const details = await Promise.all(
          pending.map(async (item: ClientAnnouncementListItem) => {
            try {
              return await getAnnouncementById(item.id);
            } catch (error) {
              console.warn("Login announcement detail could not be loaded", error);
              return item;
            }
          }),
        );

        if (!cancelled) {
          markLoginAnnouncementsShown(pending.map((item) => item.id));
          setAnnouncements(details.map(mapAnnouncementListItemToClient));
          setCurrentIndex(INITIAL_INDEX);
          setHiddenById({});
          setPersistedHiddenById({});
        }
      } catch (error) {
        console.warn("Login announcements could not be loaded", error);
      }
    }

    void loadLoginAnnouncements();

    return () => {
      cancelled = true;
    };
  }, []);

  const currentAnnouncement = announcements[currentIndex] ?? null;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === announcements.length - 1;
  const isOpen = currentAnnouncement !== null;

  const counterLabel = useMemo(
    () => `${currentIndex + 1} / ${announcements.length}`,
    [announcements.length, currentIndex],
  );

  const closeAll = () => {
    setAnnouncements([]);
    setCurrentIndex(INITIAL_INDEX);
    setHiddenById({});
    setPersistedHiddenById({});
  };

  const persistCurrentPreference = async () => {
    if (!currentAnnouncement) {
      return true;
    }

    const announcementId = currentAnnouncement.id;
    const hideAgain = Boolean(hiddenById[announcementId]);
    const wasHidden = Boolean(persistedHiddenById[announcementId]);

    if (hideAgain === wasHidden) return true;

    try {
      setSaving(true);
      await saveAnnouncementPreference(announcementId, hideAgain);
      setPersistedHiddenById((prev) => {
        if (hideAgain) {
          return {
            ...prev,
            [announcementId]: true,
          };
        }

        const next = { ...prev };
        delete next[announcementId];
        return next;
      });
      return true;
    } catch (error) {
      console.warn("Login announcement preference could not be saved", error);
      closeAll();
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const saved = await persistCurrentPreference();
    if (!saved) {
      return;
    }

    if (isLast) {
      closeAll();
      return;
    }

    setCurrentIndex((index) => index + 1);
  };

  const customHeader = currentAnnouncement ? (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <Announcement width={24} height={24} className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>
          {t("clientAnnouncements.loginModal.title")}
        </h3>
      </div>
      <span className={styles.counter}>{counterLabel}</span>
    </div>
  ) : undefined;

  const customFooter = (
    <>
      <Button
        color="secondary"
        disabled={isFirst || saving}
        onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))}
        type="button"
        variant="outlined"
      >
        {t("buttons.back")}
      </Button>
      <Button
        color="primary"
        disabled={saving}
        loading={saving}
        onClick={handleNext}
        type="button"
        variant="contained"
      >
        {isLast ? t("buttons.ok") : t("buttons.next")}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeAll}
      title=""
      customHeader={customHeader}
      customFooter={customFooter}
      dismissible={false}
      size="lg"
    >
      {currentAnnouncement && (
        <div className={styles.body}>
          {(currentAnnouncement.imageFileID || currentAnnouncement.imageUrl) && (
            <div className={styles.imageWrap}>
              {currentAnnouncement.imageFileID ? (
                <FileImage
                  fileId={currentAnnouncement.imageFileID}
                  alt=""
                  className={styles.image}
                  objectFit="contain"
                />
              ) : (
                <img
                  src={currentAnnouncement.imageUrl ?? ""}
                  alt=""
                  className={styles.image}
                />
              )}
            </div>
          )}

          <div className={styles.content}>
            <h2 className={styles.contentTitle}>{currentAnnouncement.title}</h2>
            <p className={styles.contentDescription}>
              {currentAnnouncement.description}
            </p>
          </div>

          <Checkbox
            label={t("clientAnnouncements.loginModal.hideAgain")}
            value={Boolean(hiddenById[currentAnnouncement.id])}
            onChange={(event) =>
              setHiddenById((prev) => ({
                ...prev,
                [currentAnnouncement.id]: event.target.checked,
              }))
            }
          />
        </div>
      )}
    </Modal>
  );
};

export default LoginAnnouncementsModal;
